import React from 'react';
import {
  Checkbox,
  Col,
  CountrySelect,
  Form,
  FormModal,
  Input,
  LanguageSelect,
  Row,
  Select,
  TagSelect,
  Typography,
  Upload,
  UploadContent,
} from 'components';
import { ValidationErrors } from 'constants/validations';
import { useSuppliersDetailsEdit } from './useSuppliersDetailsEdit';
import { webSiteUrlRegex } from 'utils/helpers';
import { SUPPLIER_STATUSES } from 'staticData';

const { Item } = Form;
const { Dragger } = Upload;
const { TextArea } = Input;

export interface ISupplierDetailsUpdate {
  onCancel: () => void;
  isOpen?: boolean;
}

export const SupplierDetailsUpdate = React.memo<ISupplierDetailsUpdate>((props) => {
  const { onCancel, isOpen } = props;
  const [form] = Form.useForm();

  const {
    onFinish,
    onCancelModal,
    contextHolder,
    handleFileSizeValidation,
    isSaveBtnDisabled,
    tagData,
    isLoading,
    industryData,
    supplierTypeData,
  } = useSuppliersDetailsEdit({
    onCancel,
    isOpen,
    form,
  });

  return (
    <>
      {contextHolder}
      <FormModal
        form={form}
        title={'Update Supplier Details'}
        okText={'Save'}
        open={isOpen}
        isLoading={isLoading}
        disableOkBtn={isSaveBtnDisabled}
        onCancel={onCancelModal}
        onSubmit={onFinish}
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item
              name="name"
              rules={[
                {
                  required: true,
                  message: ValidationErrors.required('Supplier Name'),
                },
              ]}
              label={'Supplier Name*'}
            >
              <Input size={'large'} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Item
              label={'Supplier Logo'}
              name="logo"
              valuePropName="fileList"
              getValueFromEvent={(e) => e?.fileList || []}
            >
              <Dragger
                beforeUpload={handleFileSizeValidation}
                onRemove={() => {
                  // checkErrors();
                }}
                accept={'.jpg, .jpeg, .png'}
                maxCount={1}
              >
                <UploadContent
                  title={'Upload or Drag and Drop IMG'}
                  message={'Max size: 5MB / Format: JPG, JPEG, PNG'}
                />
              </Dragger>
            </Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="webSite"
              label={'Supplier Website'}
              rules={[{ pattern: webSiteUrlRegex, message: 'Please enter a valid website URL.' }]}
            >
              <Input size={'large'} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="status"
              label={'Supplier Status*'}
              rules={[
                {
                  required: true,
                  message: ValidationErrors.required('Supplier Status'),
                },
              ]}
            >
              <Select options={SUPPLIER_STATUSES} size={'large'} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.status !== currentValues.status}>
              {({ getFieldValue }) =>
                getFieldValue('status') !== 'ACTIVE' ? (
                  <Form.Item
                    name="reason"
                    label="Reason"
                    rules={[{ required: true, message: ValidationErrors.required('Reason') }]}
                  >
                    <TextArea style={{ resize: 'none' }} rows={3} size={'large'} />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="mainLanguageId" label={'Main Language'}>
              <LanguageSelect size={'large'} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="mainCountryId" label={'Main Country'}>
              <CountrySelect size={'large'} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="type" label={'Type'}>
              <Select options={supplierTypeData} size={'large'} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="industryId" label={'Industry'}>
              <Select options={industryData} size={'large'} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <TagSelect data={tagData} name={'tags'} label={'Tags'} />
          </Col>
          <Col span={24}>
            <Item label={'Additional Notes'} name={'additionalNotes'}>
              <TextArea rows={4} />
            </Item>
          </Col>
          <Col span={24}>
            <Form.Item name="verified" valuePropName={'checked'}>
              <Checkbox>
                <Typography.Text style={{ fontWeight: 500, color: '#05796B' }}>Set as Verified</Typography.Text>
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </FormModal>
    </>
  );
});
